import { memo, useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import { faker } from "@faker-js/faker";
import {
    useCreateDashboardStore,
    ColorPicker,
    CreateChartState,
    CHART_NAME_PATTERN,
    CHART_ERROR_MESSAGE,
} from ".";
import DashboardCreateSelectSensorModal from "./modal/SelectDevices";
import CreateChartModal from "./modal/CreateChart";
import { COLORS } from "constant";
import { displaySensorType, parseColorToHex } from "utils/functions";
import CloseIcon from "@mui/icons-material/Close";
import { getSensorType } from "utils/sensorFunctions";
import { SensorType } from "constant/gateway";

import {
    BlueRadio,
    BlueText,
    SelectedGreenBox,
    ColorPickerBackground,
} from "components/common";
import { debounce, get, isEmpty, omit, set, size, slice } from "lodash";
import { ChartSource, ChartType } from "constant/chart";
import { getMQTTQueryByDevice } from "../utils/widgetUtil";
import {
    getWidgetConfigByWidgetType,
    WidgetType,
    getChartSourceFromWidgetType
} from "../utils/widgetConfig";
import { GATEWAY_LABEL, LDSU_UUID_LABEL } from "constant/displayLabels";

// Factory function for SingleCreateData that accepts chart type and source
const createSingleCreateData = (chart: ChartType, source: ChartSource): CreateChartState => ({
    title: "Creating New Widget(s)",
    data: [],
    columns: [
        {
            key: "chartName",
            name: "Widget Name",
        },
        {
            key: "sensorName",
            name: "Sensor",
        },
        {
            key: "sensorType",
            name: "Type",
        },
        {
            key: "gatewayName",
            name: GATEWAY_LABEL,
        },
        {
            key: "ldsuUID",
            name: LDSU_UUID_LABEL,
            className: "flex-basis-220px",
            render: (data: any) => (
                <div className="text-nowrap text-white">{data.ldsuUID}</div>
            ),
        },
        {
            key: "color",
            name: "Color",
            render: (data: any) => (
                <div className="color-picker__toggle">
                    <ColorPickerBackground
                        $inputColor={parseColorToHex(data.color)}
                    />
                </div>
            ),
        },
    ],
    transform: (data: any) => ({
        attributes: [
            {
                color: data.color,
                device_id: data.ldsuUID,
                gateway_id: data.gatewayId,
                name: data.sensorType,
                said: data.said,
                show_alert: data.alert || false,
                time_series: getMQTTQueryByDevice(data),
                unit: data.unit,
            },
        ],
        source: source,
        name: data.chartName || "Consumption Widget",
        chart: chart,
        time_range: "1d",
    }),
    validate() {
        return this.data.map((item) => ({
            path: `[name="${item.deviceId}.chart_name"]`,
            isValid: CHART_NAME_PATTERN.test(item.chartName.trim()),
            message: CHART_ERROR_MESSAGE,
        }));
    },
});

// Factory function for CombinedCreateData that accepts chart type and source
const createCombinedCreateData = (chart: ChartType, source: ChartSource): CreateChartState => ({
    title: "Creating New Widget(s)",
    multiple: false,
    data: [],
    columns: [
        {
            key: "chartName",
            name: "Widget Name",
        },
        {
            key: "type",
            name: "Sensor Type",
            render: (data: any) => data.attributes?.[0].sensorType,
        },
        {
            key: "sensors_count",
            name: "No. of Sensors",
            render: (data: any) => data.attributes?.length,
        },
    ],
    transform: (data: any) => ({
        attributes: data.attributes.map((d: any) => ({
            color: d.color,
            device_id: d.ldsuUID,
            gateway_id: d.gatewayId,
            name: d.sensorType,
            said: String(d.said),
            show_alert: false,
            time_series: getMQTTQueryByDevice(d),
            unit: d.unit,
        })),
        source: source,
        chart: chart,
        name: data.chartName || "Combined Widget",
    }),
    validate() {
        const validates = this.data.map((item) => ({
            path: "",
            isValid:
                item.chartName &&
                CHART_NAME_PATTERN.test(item.chartName?.trim()),
            message: CHART_ERROR_MESSAGE,
        }));

        if (this.data[0]?.attributes?.length < 2) {
            validates.push({
                path: "",
                isValid: false,
                message:
                    "Please select at least 2 sensors to create a combined chart.",
            });
        }

        return validates;
    },
});

const createMapping = (
    energyValue: string,
    waterValue: string
): Partial<Record<WidgetType, string>> => ({
    [WidgetType.METER_CHART_ENERGY_CONSUMPTION]: energyValue,
    [WidgetType.METER_CHART_ENERGY_COMBINE_CONSUMPTION]: energyValue,
    [WidgetType.METER_CHART_WATER_CONSUMPTION]: waterValue,
    [WidgetType.METER_CHART_WATER_COMBINE_CONSUMPTION]: waterValue,
});

const ConsumptionChartTypeMapping = createMapping("Energy", "Water");
const SensorTypeStringMapping = createMapping("active power", "water flow");


enum ConsumptionChartType {
    single = "Individual",
    combined = "Combined",
}

const CreateConsumptionComponent = () => {
    const [showSelectModal, setShowSelectModal] = useState(false);
    const [selectedDevices, setSelectedDevices] = useState<{
        [key: string]: any;
    }>({});
    const [consumptionChartType, setConsumptionChartType] = useState(
        ConsumptionChartType.single
    );
    const [chartName, setChartName] = useState("Combined Widget");
    const {
        createModalShow,
        setCreateModalShow,
        createData,
        widgetSource,
        widget,
        setWidget,
    } = useCreateDashboardStore();

    // Widget type comes from store and is updated when consumptionChartType changes.
    const isCombined = consumptionChartType === ConsumptionChartType.combined;
    const widgetType = widget?.widgetType ?? WidgetType.METER_CHART_ENERGY_CONSUMPTION;
    const widgetConfig = getWidgetConfigByWidgetType(widgetType);
    const chartSource = getChartSourceFromWidgetType(widgetType);
    const chartTypeName = ConsumptionChartTypeMapping[widgetType];
    const sensorType = SensorTypeStringMapping[widgetType];
    const title = `Sensors for ${consumptionChartType} - ${chartTypeName} consumption chart`;
    const filterSensor = widgetConfig?.filter?.(widgetType);

    const chartOptions = [
        {
            title: "Individual Consumption Charts",
            description: `Create separate consumption charts for each ${sensorType} sensor.`,
            key: ConsumptionChartType.single,
        },
        {
            title: "Combined Consumption Chart",
            description: `Data from multiple ${sensorType} sensors onto a single consumption chart for a comprehensive overview.`,
            key: ConsumptionChartType.combined,
        },
    ];

    // Update widget type when consumptionChartType changes
    useEffect(() => {
        if (widget) {
            const isEnergyWidget =
                widget.widgetType === WidgetType.METER_CHART_ENERGY_CONSUMPTION ||
                widget.widgetType === WidgetType.METER_CHART_ENERGY_COMBINE_CONSUMPTION;

            let newWidgetType: WidgetType;
            if (isEnergyWidget) {
                newWidgetType = isCombined
                    ? WidgetType.METER_CHART_ENERGY_COMBINE_CONSUMPTION
                    : WidgetType.METER_CHART_ENERGY_CONSUMPTION;
            } else {
                newWidgetType = isCombined
                    ? WidgetType.METER_CHART_WATER_COMBINE_CONSUMPTION
                    : WidgetType.METER_CHART_WATER_CONSUMPTION;
            }

            if (widget.widgetType !== newWidgetType) {
                setWidget({
                    ...widget,
                    widgetType: newWidgetType,
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [consumptionChartType, isCombined]);

    useEffect(() => {
        const name = `Combined ${chartTypeName} Consumption`;
        setChartName(name);
    }, [widgetSource, chartTypeName]);

    useEffect(() => {
        if (!size(selectedDevices)) {
            return;
        }

        if (isCombined) {
            const maxItems = widgetConfig?.maxItems ?? 4;
            const _selectedDevices = slice(
                Object.values(selectedDevices),
                0,
                maxItems
            );

            setSelectedDevices(_selectedDevices.pluck("deviceId"));
        } else {
            const data = getData();
            const createDataTemplate = createSingleCreateData(chartSource.chart, chartSource.source);
            useCreateDashboardStore.setState({
                createData: {
                    ...createDataTemplate,
                    data: data,
                    multiple: false,
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [consumptionChartType, widgetType]);

    useEffect(() => {
        if (size(selectedDevices)) {
            const data = getData();
            const createDataTemplate = isCombined
                ? createCombinedCreateData(chartSource.chart, chartSource.source)
                : createSingleCreateData(chartSource.chart, chartSource.source);
            useCreateDashboardStore.setState({
                createData: {
                    ...createDataTemplate,
                    data: data,
                    multiple: isCombined,
                },
            });
        } else {
            useCreateDashboardStore.setState({
                createData: undefined,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDevices, chartName, widgetType, isCombined]);

    const getData = () =>
        isCombined
            ? [
                {
                    chartName: chartName,
                    attributes: Object.values(selectedDevices),
                },
            ]
            : Object.values(selectedDevices);

    const getChartName = (sensor: any) => {
        const sensorType = getSensorType({ CLS: sensor.cls });
        return `${sensorType === SensorType.WaterConsumption ? "Water" : "Energy"
            } Consumption`;
    };

    const onRemove = (device: any) => {
        setSelectedDevices((prev: any) => omit(prev, device.deviceId));
    };

    const onChartNameChange = debounce((event) => {
        const { name, value } = event.target;
        setSelectedDevices((prev) => set(prev, name, value));
    }, 500);

    return (
        <>
            <Row>
                <Col sm={12}>
                    <p>{chartTypeName} consumption chart:</p>
                </Col>
                <Col md={12}>
                    <div>
                        {chartOptions.map(({ key, title, description }) => {
                            return (
                                <SelectedGreenBox
                                    className={`mt-2 w-100 ${key === consumptionChartType &&
                                        "selected"
                                        }`}
                                    key={key}
                                    onClick={() => {
                                        setConsumptionChartType(key);
                                    }}
                                >
                                    <BlueRadio
                                        name="chart-type"
                                        custom
                                        type="radio"
                                        value={key}
                                        id={`chart-type-${key}`}
                                        checked={key === consumptionChartType}
                                        label={
                                            <div className="d-flex align-items-center ml-2">
                                                <div className="ml-2">
                                                    <span className="d-block">
                                                        {title}
                                                    </span>
                                                    <BlueText>
                                                        {description}
                                                    </BlueText>
                                                </div>
                                            </div>
                                        }
                                        onChange={() => {
                                            setConsumptionChartType(key);
                                        }}
                                    />
                                </SelectedGreenBox>
                            );
                        })}
                    </div>
                    <hr className="my-4" />
                </Col>
            </Row>
            <Row>
                <Col sm="12">
                    {isCombined && (
                        <Form.Group>
                            <Form.Label>Widget Name</Form.Label>
                            <Form.Control
                                name="name"
                                type="text"
                                value={chartName}
                                placeholder=""
                                onChange={(event) =>
                                    setChartName(event.target.value)
                                }
                            />
                        </Form.Group>
                    )}
                    <p>{title}</p>
                    <div className="mt-2">
                        <Button
                            className="mr-2"
                            variant="primary"
                            onClick={() => setShowSelectModal(true)}
                        >
                            Select Sensors
                        </Button>
                        {isCombined && (
                            <BlueText>
                                You can add max{" "}
                                {widgetConfig?.maxItems ?? 4} sensors
                            </BlueText>
                        )}
                    </div>
                    {isCombined && size(selectedDevices) === 1 && (
                        <p className="mt-2 text-danger">
                            Please select at least two sensors to create a
                            combined consumption chart.
                        </p>
                    )}

                    <DashboardCreateSelectSensorModal
                        show={showSelectModal}
                        allowSensorFilter={false}
                        onHide={() => setShowSelectModal(false)}
                        values={selectedDevices}
                        onSave={(event, data) =>
                            setSelectedDevices(
                                Object.values(data)
                                    .map((item: any, index: number) => ({
                                        ...item,
                                        chartName: getChartName(item),
                                        color:
                                            COLORS[index] || faker.color.rgb(),
                                        alert: false,
                                    }))
                                    .pluck("deviceId")
                            )
                        }
                        deviceType="Sensor"
                        maxDevices={isCombined ? widgetConfig?.maxItems ?? 4 : undefined}
                        filter={filterSensor}
                    />
                </Col>
            </Row>
            <Row className="mt-4 cstm-table">
                <Col sm={12}>
                    {!!Object.values(selectedDevices).length && (
                        <div className="table-head">
                            <Row className="no-checkbox">
                                <Col md={2}>Sensor</Col>
                                {!isCombined && <Col md={2}>Widget Name</Col>}
                                <Col md={2}>Type</Col>
                                <Col md={isCombined ? 4 : 2} sm={2}>
                                    {GATEWAY_LABEL}
                                </Col>
                                <Col md={2}>{LDSU_UUID_LABEL}</Col>
                                <Col lg={1} md={1} sm={2}>
                                    Color
                                </Col>
                                <Col sm={1}></Col>
                            </Row>
                        </div>
                    )}
                    {Object.values(selectedDevices).map((device, index) => (
                        <div
                            key={device.deviceId}
                            id={device.deviceId}
                            className="table-row bg-secondary"
                        >
                            <Row className="no-checkbox">
                                <Col md={2}>{device.sensorName}</Col>
                                {!isCombined && (
                                    <Col md={2}>
                                        <Form.Control
                                            name={`${device.deviceId}.chartName`}
                                            type="text"
                                            defaultValue={device.chartName}
                                            placeholder=""
                                            onChange={onChartNameChange}
                                        />
                                    </Col>
                                )}
                                <Col md={2}>{displaySensorType(device)}</Col>
                                <Col md={isCombined ? 4 : 2} sm={2}>
                                    {device.gatewayName}
                                </Col>
                                <Col md={2}>{device.ldsuUID}</Col>
                                <Col sm={2} lg={1}>
                                    <ColorPicker
                                        defaultValue={get(
                                            selectedDevices,
                                            `${device.deviceId}.color`,
                                            COLORS[index]
                                        )}
                                        onChange={(event, color) =>
                                            setSelectedDevices((prev: any) => {
                                                const newDevices = { ...prev };
                                                set(
                                                    newDevices,
                                                    `${device.deviceId}.color`,
                                                    color
                                                );

                                                return newDevices;
                                            })
                                        }
                                    />
                                </Col>
                                <Col
                                    sm={1}
                                    className="d-flex justify-content-end"
                                >
                                    <CloseIcon
                                        className="cursor-pointer mr-2 text-primary-red-1"
                                        onClick={() => onRemove(device)}
                                        onKeyDown={() => onRemove(device)}
                                    />
                                </Col>
                            </Row>
                        </div>
                    ))}
                </Col>
            </Row>

            <CreateChartModal
                show={createModalShow && !isEmpty(createData)}
                onHide={() => setCreateModalShow(false)}
            />
        </>
    );
};

export default memo(CreateConsumptionComponent);
